import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from "@angular/core";

/**
 * This component wraps two input fields. The first one is of type 'range' and sets the value of
 * the second, that also act as input to the first one. Both of them emit the given value.
 */
@Component({
  selector: "vf-input-range-slider",
  templateUrl: "./vf-input-range-slider.component.html",
  styleUrls: ["./vf-input-range-slider.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VfInputRangeSliderComponent implements OnChanges {
  /**
   * The initial value of the slider.
   */
  @Input() initialValue!: number;
  /**
   * Toggle state of inputs
   * */
  @Input() disabled = false;
  /**
   * Emit new values given by the input range
   */
  @Output() rangeSliderEvent = new EventEmitter<number>();
  /**
   * Get the input element to initially calculate size of background
   */
  @ViewChild("rangeInput", { read: ElementRef })
  private rangeInput!: ElementRef<HTMLInputElement>;

  sliderRange: number[] = [20, 75, 150, 225, 300];
  rangeMin = 20;
  rangeMax = 300;

  constructor() {}

  ngOnChanges(): void {
    if (this.rangeInput) {
      const bgSizeX = this.toPercentOfRange(this.initialValue, this.rangeMin, this.rangeMax);
      this.rangeInput.nativeElement.style.backgroundSize = `${bgSizeX}% 100%`;
    }
  }

  private clamp(val: number, rangeMin: number, rangeMax: number): number {
    return val < rangeMin ? rangeMin : val > rangeMax ? rangeMax : val;
  }

  toPercentOfRange(val: number, rangeMin: number, rangeMax: number): number {
    return (100 * (val - rangeMin)) / (rangeMax - rangeMin);
  }

  toBgSizePerc(val: number): string {
    return `${val}% 100%`;
  }
  /**
   * To extract, transform and emit the house area value including adjusting backgroundSize dynamically
   */
  onRangeInputChange(inputElm: HTMLInputElement, outputElm: HTMLInputElement) {
    const inputWithinRange = this.clamp(inputElm.valueAsNumber, this.rangeMin, this.rangeMax);
    const outputWithinRange = this.clamp(inputWithinRange, this.rangeMin, this.rangeMax);
    const bgSizeX = this.toPercentOfRange(outputWithinRange, this.rangeMin, this.rangeMax);
    outputElm.value = inputWithinRange.toString();
    inputElm.style.backgroundSize = `${bgSizeX}% 100%`;
    this.emitCurrent(outputElm.valueAsNumber);
  }

  /**
   * To extract, transform and emit the house area value including adjusting backgroundSize dynamically
   */
  onRangeOutputChange(inputElm: HTMLInputElement, outputElm: HTMLInputElement) {
    const outputWithinRange = this.clamp(outputElm.valueAsNumber, this.rangeMin, this.rangeMax);
    const bgSizeX = this.toPercentOfRange(outputWithinRange, this.rangeMin, this.rangeMax);
    inputElm.value = outputWithinRange.toString();
    inputElm.style.backgroundSize = `${bgSizeX}% 100%`;
    this.emitCurrent(inputElm.valueAsNumber);
  }

  /**
   * This method uses the value from the input slider to position an output element
   * within the width of a parent element. The width of the output element is included
   * into the width, so that it is right aligned for high value, and left aligned for low.
   * @param val value of input range
   * @param min input range min
   * @param max input range max
   * @param wrapOutputElm element that shall be placed within a horizontal area
   * @param wrapRangeSliderElm element defining the width of a scaled ragnge
   * @param marginRightPx adjust the length to create a margin
   */
  rangeValueToAbsolutePosOfParentWidth(
    val: number,
    min: number,
    max: number,
    wrapOutputElm: HTMLElement,
    wrapRangeSliderElm: HTMLElement,
    minLeftPx = 0,
    marginRightPx = 0
  ) {
    const wrapOutputWidth = wrapOutputElm.getBoundingClientRect().width;
    const wrapRangeSliderWidth = wrapRangeSliderElm.getBoundingClientRect().width;
    const ratioOfRange = this.toPercentOfRange(val, min, max) / 100;
    let pxPosAtWrapRange = ratioOfRange * (wrapRangeSliderWidth - wrapOutputWidth);
    const maxRightPx = wrapRangeSliderWidth - wrapOutputWidth - marginRightPx;
    // clamp position to minLeft maxLeft
    pxPosAtWrapRange = this.clamp(pxPosAtWrapRange, minLeftPx, maxRightPx);
    return `${Math.round(pxPosAtWrapRange)}px`;
  }

  // To emit the current slider value back to parent component
  emitCurrent(valueOnRange: number) {
    this.rangeSliderEvent.emit(valueOnRange);
  }

  // Block external eventhandlers from thrashing the input range.
  mouseMoveHandler(e: MouseEvent) {
    e.stopPropagation();
  }
}
